import React, { useState } from 'react';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon, XMarkIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { Check } from 'lucide-react';
import { updateProject } from '../../utils/supabaseClient';

// State-specific components
import LouisianaPayAdjustment from './states/Louisiana';
import IndianaPayAdjustment from './states/Indiana';
import IowaPayAdjustment from './states/Iowa';
import MinnesotaPayAdjustment from './states/Minnesota';
import PennsylvaniaPayAdjustment from './states/Pennsylvania';
import OhioPayAdjustment from './states/Ohio';
import WisconsinPayAdjustment from './states/Wisconsin';
import NebraskaPayAdjustment from './states/Nebraska';

// Registry of supported states
const stateComponents = {
  LA: LouisianaPayAdjustment,
  IN: IndianaPayAdjustment,
  IA: IowaPayAdjustment,
  MN: MinnesotaPayAdjustment,
  PA: PennsylvaniaPayAdjustment,
  OH: OhioPayAdjustment,
  WI: WisconsinPayAdjustment,
  NE: NebraskaPayAdjustment,
};

// State metadata for display
const stateInfo = {
  LA: { name: 'Louisiana', spec: 'DOTD Table 502-8' },
  FL: { name: 'Florida', spec: 'FDOT Section 352' },
  IA: { name: 'Iowa', spec: 'Iowa DOT' },
  MN: { name: 'Minnesota', spec: 'MnDOT' },
  PA: { name: 'Pennsylvania', spec: 'PennDOT Publication 408/2020' },
  OH: { name: 'Ohio', spec: 'ODOT' },
  WI: { name: 'Wisconsin', spec: 'WisDOT' },
  NE: { name: 'Nebraska', spec: 'AC Pavement Smoothness' },
  IN: { name: 'Indiana', spec: 'INDOT' },
};

// Supported states for combobox
const SUPPORTED_STATES = Object.keys(stateComponents).map(code => ({
  value: code,
  label: stateInfo[code]?.name || code,
  spec: stateInfo[code]?.spec,
}));

// List of supported states for the UI
export const getSupportedStates = () => {
  return Object.keys(stateComponents).map(code => ({
    code,
    name: stateInfo[code]?.name || code,
    spec: stateInfo[code]?.spec,
  }));
};

// Check if a state is supported
export const isStateSupported = (stateCode) => {
  return stateCode in stateComponents;
};

/**
 * State selection combobox - matches ProjectConfiguration style
 */
const StateCombobox = ({ value, onChange }) => {
  const [query, setQuery] = useState('');
  const buttonRef = React.useRef(null);

  const selectedState = SUPPORTED_STATES.find(s => s.value === value) || null;

  const filteredStates = query === ''
    ? SUPPORTED_STATES
    : SUPPORTED_STATES.filter((state) => {
        const searchLower = query.toLowerCase();
        return state.label.toLowerCase().includes(searchLower) ||
               state.value.toLowerCase().includes(searchLower);
      });

  return (
    <Combobox
      as="div"
      value={selectedState}
      onChange={(state) => {
        setQuery('');
        onChange(state?.value || '');
      }}
    >
      <div className="relative">
        <ComboboxInput
          className="block w-full rounded-sm bg-white py-2 pr-10 pl-3 text-sm text-gray-900 border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => buttonRef.current?.click()}
          onBlur={() => setQuery('')}
          displayValue={(state) => state?.label || ''}
          placeholder="Search states..."
        />
        <ComboboxButton ref={buttonRef} className="absolute inset-y-0 right-0 flex items-center rounded-r-sm px-2 focus:outline-none">
          <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </ComboboxButton>

        <ComboboxOptions
          transition
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-sm bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {filteredStates.length === 0 && query !== '' ? (
            <div className="px-3 py-2 text-gray-500">No states found</div>
          ) : (
            filteredStates.map((state) => (
              <ComboboxOption
                key={state.value}
                value={state}
                className="cursor-pointer select-none px-3 py-2 text-gray-900 data-[focus]:bg-red-50 data-[focus]:text-red-900"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">{state.label}</span>
                    <span className="ml-2 text-gray-500 text-xs">{state.spec}</span>
                  </div>
                  {state.value === value && (
                    <Check className="h-4 w-4 text-red-600" />
                  )}
                </div>
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

/**
 * Modal for setting project state
 */
const SetStateModal = ({ isOpen, onClose, projectId, onStateSaved }) => {
  const [selectedState, setSelectedState] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!selectedState) return;

    setSaving(true);
    setError(null);

    try {
      await updateProject(projectId, { state: selectedState });
      onStateSaved(selectedState);
      onClose();
    } catch (err) {
      console.error('Failed to update project state:', err);
      setError('Failed to save state. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-sm bg-white shadow-xl rounded-sm">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Set Project State</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-4">
            {error && (
              <div className="mb-3 bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 rounded-sm">
                {error}
              </div>
            )}

            <label className="block text-xs font-medium text-gray-700 mb-1">
              State
            </label>
            <StateCombobox
              value={selectedState}
              onChange={setSelectedState}
            />
            <p className="mt-2 text-xs text-gray-500">
              Select a state with pay calculator support.
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedState || saving}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * PayAdjustmentPanel
 */
const PayAdjustmentPanel = ({ project, session, segments, alrDefects, surfaceEvents, isReversed, onProjectUpdate }) => {
  const [showStateModal, setShowStateModal] = useState(false);
  const stateCode = project?.state;
  const StateComponent = stateCode ? stateComponents[stateCode] : null;

  const handleStateSaved = (newState) => {
    if (onProjectUpdate) {
      onProjectUpdate({ ...project, state: newState });
    } else {
      window.location.reload();
    }
  };

  // No state configured
  if (!stateCode) {
    return (
      <>
        <StateNotConfigured onSetState={() => setShowStateModal(true)} />
        <SetStateModal
          isOpen={showStateModal}
          onClose={() => setShowStateModal(false)}
          projectId={project?.id}
          onStateSaved={handleStateSaved}
        />
      </>
    );
  }

  // State not yet supported
  if (!StateComponent) {
    return (
      <EmptyState
        title="Pay Adjustment Not Available"
        description={`Smoothness pay adjustment calculations are not yet supported for ${stateInfo[stateCode]?.name || stateCode}.`}
      />
    );
  }

  return <StateComponent project={project} session={session} segments={segments} alrDefects={alrDefects} surfaceEvents={surfaceEvents} isReversed={isReversed} />;
};

/**
 * State not configured view
 */
const StateNotConfigured = ({ onSetState }) => {
  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 h-20">
        <h3 className="text-lg font-medium text-gray-900">Smoothness Pay Adjustment</h3>
        <p className="text-sm text-gray-500 mt-0.5">Calculate payment adjustments based on IRI results</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-sm mb-4">
            <MapPinIcon className="w-6 h-6 text-gray-500" />
          </div>

          <h4 className="text-base font-medium text-gray-900 mb-2">State Not Configured</h4>
          <p className="text-sm text-gray-500 mb-4">
            Select a state to enable pay adjustment calculations based on DOT specifications.
          </p>

          <button
            onClick={onSetState}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-sm"
          >
            Set State
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Empty state for unsupported states
 */
const EmptyState = ({ title, description }) => {
  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 h-20">
        <h3 className="text-lg font-medium text-gray-900">Smoothness Pay Adjustment</h3>
        <p className="text-sm text-gray-500 mt-0.5">Calculate payment adjustments based on IRI results</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-50 rounded-sm mb-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
          </div>

          <h4 className="text-base font-medium text-gray-900 mb-2">{title}</h4>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
    </div>
  );
};

export default PayAdjustmentPanel;
